import { ScannerScheduledDowntimeAPI } from '@API/data-services/scanner-scheduled-downtime-service'
import { RootState } from '@AppState/redux-store'
import { AuthScopes, ResponseError, ScheduledDowntime } from '@Generated/back-office-client/src'
import {
  Badge,
  type BadgeProps,
  Button,
  Container,
  Flex,
  Heading,
  IconButton,
  Section,
  Switch,
  Table,
  Text,
} from '@radix-ui/themes'
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons'
import { useQuery } from '@tanstack/react-query'
import { formatInTimeZone } from 'date-fns-tz'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { DeleteConfirm } from './delete-confirm'
import { formatDaysOfWeek, formatTime } from './types'
import { UpsertDrawer } from './upsert-drawer'

enum OneOffStatus {
  ACTIVE = 'Active',
  UPCOMING = 'Upcoming',
  PAST = 'Past',
}

const computeOneOffStatus = (row: ScheduledDowntime, now: number): OneOffStatus => {
  const start = row.startsAt ? row.startsAt.getTime() : 0
  const end = row.endsAt ? row.endsAt.getTime() : 0
  if (now < start) {
    return OneOffStatus.UPCOMING
  }
  if (now >= end) {
    return OneOffStatus.PAST
  }
  return OneOffStatus.ACTIVE
}

const statusColor = (status: OneOffStatus): BadgeProps['color'] => {
  if (status === OneOffStatus.ACTIVE) {
    return 'green'
  }
  if (status === OneOffStatus.UPCOMING) {
    return 'blue'
  }
  return 'gray'
}

export const ScannerScheduledDowntime = () => {
  const { activeCustomerMetadata, currentUser } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'
  const canWrite = !!currentUser?.scopes.includes(AuthScopes.writeticket_scanner_downtime)

  const [includePast, setIncludePast] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ScheduledDowntime | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ScheduledDowntime | null>(null)

  const { data, error, isLoading } = useQuery({
    queryKey: ['scanner-scheduled-downtime', includePast],
    queryFn: () => ScannerScheduledDowntimeAPI.list(includePast),
    refetchInterval: 60_000,
  })

  // TODO status banner once available v2 status endpoint exposed via back-office-service

  const errorMessage = useMemo(() => {
    if (!error) {
      return null
    }
    if (error instanceof ResponseError) {
      return error.message
    }
    return 'An unexpected error occurred.'
  }, [error])

  const recurring = useMemo(() => data?.recurring ?? [], [data])
  const oneOff = useMemo(() => data?.oneOff ?? [], [data])

  const now = Date.now()

  const openCreate = () => {
    setEditTarget(null)
    setDrawerOpen(true)
  }
  const openEdit = (row: ScheduledDowntime) => {
    setEditTarget(row)
    setDrawerOpen(true)
  }

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="3">
          <Flex justify="between" align="center">
            <Heading as="h1" size="5">
              Scanner Scheduled Downtime
            </Heading>
            {canWrite ? <Button onClick={openCreate}>Add window</Button> : null}
          </Flex>
          <Text size="2" color="gray">
            Times shown in customer timezone ({timezone}).
          </Text>

          <Flex align="center" gap="2">
            <Switch
              checked={includePast}
              onCheckedChange={(checked) => setIncludePast(checked === true)}
            />
            <Text size="2">Show past one-off windows</Text>
          </Flex>

          {errorMessage ? <Text color="red">{errorMessage}</Text> : null}
          {isLoading ? <Text size="2">Loading...</Text> : null}

          <Heading as="h2" size="3" mt="3">
            Recurring
          </Heading>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Days</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Start</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>End</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
                {canWrite ? <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell> : null}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {recurring.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={canWrite ? 5 : 4}>
                    <Text size="2" color="gray">
                      No recurring windows configured.
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {recurring.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>{formatDaysOfWeek(row.daysOfWeek)}</Table.Cell>
                  <Table.Cell>{formatTime(row.startTime)}</Table.Cell>
                  <Table.Cell>{formatTime(row.endTime)}</Table.Cell>
                  <Table.Cell>{row.reason || '-'}</Table.Cell>
                  {canWrite ? (
                    <Table.Cell>
                      <Flex gap="2">
                        <IconButton variant="ghost" onClick={() => openEdit(row)}>
                          <Pencil1Icon />
                        </IconButton>
                        <IconButton
                          variant="ghost"
                          color="red"
                          onClick={() => setDeleteTarget(row)}>
                          <TrashIcon />
                        </IconButton>
                      </Flex>
                    </Table.Cell>
                  ) : null}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <Heading as="h2" size="3" mt="3">
            One-off
          </Heading>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Starts</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Ends</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
                {canWrite ? <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell> : null}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {oneOff.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={canWrite ? 5 : 4}>
                    <Text size="2" color="gray">
                      No one-off windows configured.
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {oneOff.map((row) => {
                const status = computeOneOffStatus(row, now)
                return (
                  <Table.Row key={row.id}>
                    <Table.Cell>
                      <Badge color={statusColor(status)}>{status}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {row.startsAt ? formatInTimeZone(row.startsAt, timezone, 'PP p') : '-'}
                    </Table.Cell>
                    <Table.Cell>
                      {row.endsAt ? formatInTimeZone(row.endsAt, timezone, 'PP p') : '-'}
                    </Table.Cell>
                    <Table.Cell>{row.reason || '-'}</Table.Cell>
                    {canWrite ? (
                      <Table.Cell>
                        <Flex gap="2">
                          <IconButton variant="ghost" onClick={() => openEdit(row)}>
                            <Pencil1Icon />
                          </IconButton>
                          <IconButton
                            variant="ghost"
                            color="red"
                            onClick={() => setDeleteTarget(row)}>
                            <TrashIcon />
                          </IconButton>
                        </Flex>
                      </Table.Cell>
                    ) : null}
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Flex>
      </Section>

      {canWrite ? (
        <UpsertDrawer open={drawerOpen} onOpenChange={setDrawerOpen} editTarget={editTarget} />
      ) : null}
      <DeleteConfirm target={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </Container>
  )
}
